import { RECORD_TYPES, type RecordFind, type RecordsState } from '@/components/alerts/types'
import { dayOf } from '@/components/alerts/ui'
import { TopicGlyph } from '@/components/gg/TopicGlyph'

/** "Filed Tue, Oct 13" · "Released Thu, Oct 22" · "On the release calendar" */
export function filedLine(f: RecordFind): string {
  if (f.type === 'release') return f.doc_filed ? 'Released ' + dayOf(f.doc_filed) : 'On the release calendar'
  return f.doc_filed ? 'Filed ' + dayOf(f.doc_filed) : 'Checked ' + dayOf(f.doc_checked)
}

export function FindTag({ f }: { f: RecordFind }) {
  const t = RECORD_TYPES[f.type] ?? { name: 'Record', topic: 'civic-government' }
  return <span className="gga-tag"><TopicGlyph topic={t.topic} size={14} />{t.name}<span className="d">·</span><span className="c">{f.place}</span></span>
}

export function DocLink({ f, className }: { f: RecordFind; className?: string }) {
  return f.doc_url
    ? <a className={className} href={f.doc_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{f.doc_name}</a>
    : <span className={className}>{f.doc_name}</span>
}

/** A find in the queue: type and place, the filed date, plain facts, the next date, the document, Dismiss and Build · 12. */
export function FindCard({ f, selected, onOpen, onDismiss, onBuild, cost }: {
  f: RecordFind; selected?: boolean; onOpen: () => void; onDismiss: () => void; onBuild: () => void; cost: number
}) {
  return (
    <div className={`gga-card${selected ? ' sel' : ''}`} role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}>
      <FindTag f={f} />{f.decided && <span className="gga-upd">Update</span>}
      <div className="gga-when">{filedLine(f)}</div>
      <div className="gga-hl">{f.title}</div>
      {f.facts.map((p, i) => <div className="gga-fact" key={i}>{p}</div>)}
      {f.next_date && <div className="gga-nx"><b>{dayOf(f.next_date)}</b>{f.next_what}</div>}
      <div className="gga-foot">
        <DocLink f={f} className="gga-src" />
        <button type="button" className="gga-btn" onClick={(e) => { e.stopPropagation(); onDismiss() }}>Dismiss</button>
        <button type="button" className="gga-btn gga-btn--b" onClick={(e) => { e.stopPropagation(); onBuild() }}>Build<span className="seed-badge"> · {cost}</span></button>
      </div>
    </div>
  )
}

/** "Records this week 3 / 12 sources". Tap it for Settings › Records. */
export function RecordsBadge({ r, onOpen }: { r: RecordsState; onOpen: () => void }) {
  return (
    <button type="button" className="gga-wb" onClick={onOpen} title="Public records settings">
      <div className="k">Records this week</div>
      <div className={`v${r.left === 0 ? ' zero' : ''}`}><b>{r.spent}</b><small>/ {r.cap} sources</small></div>
    </button>
  )
}

/** The soonest next date first (Dana: tomorrow's agenda sat under a license), then newest. */
export function sortFinds(list: RecordFind[]): RecordFind[] {
  const nd = (f: RecordFind) => (f.next_date ? Date.parse(f.next_date + 'T12:00:00') : Infinity)
  return list.slice().sort((a, b) => (nd(a) - nd(b)) || (Date.parse(b.created_at) - Date.parse(a.created_at)))
}
